import React, { useState, useEffect } from 'react';
import { buildApiUrl, apiConfig } from '../config/api';

interface MaintenanceData {
  person_id: string;
  first_name: string;
  last_name: string;
  current_membership: string;
  status: string;
  created_at: string;
  group_count: number;
  last_group_activity: string;
  recent_checkins: number;
  completed_discover_grace: number;
  completed_first_time_visitor: number;
  leadership_count: number;
  suggested_membership: string;
  suggestion_reason: string;
}

interface SummaryStats {
  suggested_membership: string;
  count: string;
}

interface MaintenanceResponse {
  success: boolean;
  maintenanceData: MaintenanceData[];
  summaryStats: SummaryStats[];
  criteria: {
    leader: string;
    engagedMember: string;
    member: string;
    attendee: string;
    visitor: string;
  };
}

const MembershipMaintenance: React.FC = () => {
  const [data, setData] = useState<MaintenanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('priority');
  const [exportingCSV, setExportingCSV] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchMaintenanceData();
  }, []);

  const fetchMaintenanceData = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(apiConfig.endpoints.admin.membershipMaintenance), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch maintenance data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const exportMembershipRecommendations = async () => {
    try {
      setExportingCSV(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(buildApiUrl(apiConfig.endpoints.admin.exportMembershipRecommendations), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      // Get the CSV content
      const csvContent = await response.text();
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `membership-recommendations-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage('CSV file downloaded successfully! You can now import this into Planning Center Online.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while exporting CSV');
    } finally {
      setExportingCSV(false);
    }
  };

  const getMembershipBadgeColor = (membership: string) => {
    switch (membership) {
      case 'Leader':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Engaged Member':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Member':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Attendee':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Visitor':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getPriorityOrder = (membership: string) => {
    switch (membership) {
      case 'Leader': return 1;
      case 'Engaged Member': return 2;
      case 'Member': return 3;
      case 'Attendee': return 4;
      case 'Visitor': return 5;
      default: return 6;
    }
  };

  const filteredData = data?.maintenanceData.filter(item => {
    if (filter === 'all') return true;
    return item.suggested_membership === filter;
  }) || [];

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortBy === 'priority') {
      const priorityA = getPriorityOrder(a.suggested_membership);
      const priorityB = getPriorityOrder(b.suggested_membership);
      if (priorityA !== priorityB) return priorityA - priorityB;
      return a.last_name.localeCompare(b.last_name);
    } else if (sortBy === 'name') {
      return a.last_name.localeCompare(b.last_name);
    } else if (sortBy === 'current') {
      return a.current_membership.localeCompare(b.current_membership);
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading membership maintenance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={fetchMaintenanceData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Membership Type Maintenance</h1>
          <p className="mt-2 text-gray-600">
            Review and manage membership type recommendations based on engagement data
          </p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-green-400">✅</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="text-green-400 hover:text-green-600"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400">⚠️</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Summary Statistics */}
        {data?.summaryStats && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Summary Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.summaryStats.map((stat) => (
                <div key={stat.suggested_membership} className="bg-white p-4 rounded-lg shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        {stat.suggested_membership || 'No Type'}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getMembershipBadgeColor(stat.suggested_membership)}`}>
                      {stat.suggested_membership || 'No Type'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Criteria Information */}
        {data?.criteria && (
          <div className="mb-8 bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Membership Type Criteria</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-red-800 mb-2">👑 Leader (Top Leaders)</h3>
                <p className="text-sm text-gray-600">{data.criteria.leader}</p>
              </div>
              <div>
                <h3 className="font-medium text-purple-800 mb-2">⭐ Engaged Member</h3>
                <p className="text-sm text-gray-600">{data.criteria.engagedMember}</p>
              </div>
              <div>
                <h3 className="font-medium text-blue-800 mb-2">👑 Member</h3>
                <p className="text-sm text-gray-600">{data.criteria.member}</p>
              </div>
              <div>
                <h3 className="font-medium text-green-800 mb-2">📅 Attendee</h3>
                <p className="text-sm text-gray-600">{data.criteria.attendee}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">👥 Visitor</h3>
                <p className="text-sm text-gray-600">{data.criteria.visitor}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Controls */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <div className="flex flex-wrap gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Suggested Type:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Types</option>
                <option value="Leader">Leader</option>
                <option value="Engaged Member">Engaged Member</option>
                <option value="Member">Member</option>
                <option value="Attendee">Attendee</option>
                <option value="Visitor">Visitor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="priority">Priority</option>
                <option value="name">Name</option>
                <option value="current">Current Membership</option>
              </select>
            </div>
            <div className="ml-auto flex space-x-2">
              <button
                onClick={exportMembershipRecommendations}
                disabled={exportingCSV}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  exportingCSV
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {exportingCSV ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                    Exporting...
                  </span>
                ) : (
                  '📥 Export CSV'
                )}
              </button>
              <button
                onClick={fetchMaintenanceData}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Refresh Data
              </button>
            </div>
          </div>
        </div>

        {/* Maintenance Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              People Needing Membership Type Updates ({sortedData.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Person
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current → Suggested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Engagement Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((person) => (
                  <tr key={person.person_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {person.first_name} {person.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {person.person_id}
                        </div>
                        <div className="text-sm text-gray-500">
                          Member since: {new Date(person.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getMembershipBadgeColor(person.current_membership)}`}>
                          {person.current_membership || 'No Type'}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getMembershipBadgeColor(person.suggested_membership)}`}>
                          {person.suggested_membership}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="space-y-1">
                        <div>Groups: {person.group_count}</div>
                        <div>Leadership: {person.leadership_count}</div>
                        <div>Recent Check-ins: {person.recent_checkins}</div>
                        <div className="flex space-x-2">
                          {person.completed_discover_grace === 1 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Discover Grace</span>
                          )}
                          {person.completed_first_time_visitor === 1 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">First Time Visitor</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {person.suggestion_reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">📥 CSV Export for PCO Import</h3>
          <div className="text-blue-800 space-y-2">
            <p>• <strong>Export CSV:</strong> Click "Export CSV" to download all membership recommendations</p>
            <p>• <strong>Review Data:</strong> Check the engagement data and reasoning before importing</p>
            <p>• <strong>Import to PCO:</strong> Use Planning Center's bulk import feature to update membership types</p>
            <p>• <strong>Priority Order:</strong> Focus on Leaders and Engaged Members first, then Members and Attendees</p>
            <p>• <strong>Data Sync:</strong> Changes will automatically sync to the Parable database within a few minutes</p>
            <p>• <strong>Audit Trail:</strong> All changes are logged in Planning Center Online for tracking</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipMaintenance;
